import React, { useState, useEffect, useMemo } from 'react';
import './Login.css';
import { API_BASE_URL, logger, apiFetch, enableDemoMode, INCLUDE_DEMO_LINK, INCLUDE_ABOUT_LINK } from './config';
import { useSession } from './context/SessionContext';

function Login({ onLoginSuccess, logAction }) {
  const { initializeSession } = useSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // ==================== Handle Stripe redirect URL params ====================
  // Note: Primary Stripe redirect handling is in App.js (runs when user is already logged in).
  // This handles the case where user's session expired during checkout and lands on login page.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscriptionParam = params.get('subscription');

    if (subscriptionParam === 'success') {
      setSuccessMessage('✅ Payment successful! Please log in to continue.');
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (subscriptionParam === 'canceled') {
      setError('Payment was canceled. You can try again after logging in.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    // Clear stale subscription data before fresh login
    localStorage.removeItem('subscriptionStatus');
    localStorage.removeItem('canAccess');
    localStorage.removeItem('trialDaysLeft');

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/loginGetAll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const text = await response.text(); // Read as text first
        const userData = text ? JSON.parse(text) : null; // Parse only if not empty
        
        // Check if login was successful (not null)
        if (userData && userData.role) {

          // ==================== Check if user is active ====================
          const isActive = userData.active === '1' || userData.active === 'true' || userData.active === true || userData.active === 1;
          if (!isActive) {
            await logAction('Login Blocked', 'Account not active', '', username);
            setError('Your account is not active. Please contact your instructor.');
            setLoading(false);
            return;
          }

          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('username', username);
          localStorage.setItem('userRole', userData.role);
          localStorage.removeItem('demoMode'); // always clear demo flag on real login
          if (userData.token) {
            localStorage.setItem('jwtToken', userData.token);
          }
          
          // Store class_section and university for curriculum folder path
          if (userData.class_section) {
            localStorage.setItem('class_section', userData.class_section);
          }
          if (userData.university) {
            localStorage.setItem('university', userData.university);
          }
          
          // Store semester from backend
          if (userData.semester) {
            localStorage.setItem('semester', userData.semester);
          }

          // ==================== Check subscription ====================
          const subscriptionRequired = userData.subscription_required === '1' || userData.subscription_required === 'true' || userData.subscription_required === true || userData.subscription_required === 1;
          localStorage.setItem('subscriptionRequired', subscriptionRequired ? 'true' : 'false');

          if (subscriptionRequired) {
            // Call subscription status API
            try {
              const subResponse = await apiFetch(`${API_BASE_URL}/api/subscription/status?username=${encodeURIComponent(username)}`);
              if (subResponse.ok) {
                const subData = await subResponse.json();
                localStorage.setItem('subscriptionStatus', subData.subscription_status || 'none');
                localStorage.setItem('canAccess', subData.can_access ? 'true' : 'false');
                if (subData.trial_days_left !== undefined && subData.trial_days_left !== null) {
                  localStorage.setItem('trialDaysLeft', String(subData.trial_days_left));
                }
                logger.info('Subscription status:', subData.subscription_status, '| Can access:', subData.can_access, '| Trial days left:', subData.trial_days_left);
              }
            } catch (subErr) {
              logger.error('Error checking subscription status:', subErr);
              // Default to allowing access if subscription check fails (graceful degradation)
              localStorage.setItem('canAccess', 'true');
              localStorage.setItem('subscriptionStatus', 'unknown');
            }
          } else {
            // User is exempt — no subscription needed
            localStorage.setItem('subscriptionStatus', 'exempt');
            localStorage.setItem('canAccess', 'true');
            localStorage.setItem('trialDaysLeft', '-1');
          }
          
          // Initialize session timer with user data
          initializeSession(userData);
          
          // Initialize gamification points & record streak (fire-and-forget)
          // Guard: only call streak API once per calendar day per user
          const streakKey = `streakRecorded_${username}`;
          // Use local date (not UTC) to avoid timezone issues for evening logins
          const now = new Date();
          const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
          const alreadyRecordedToday = localStorage.getItem(streakKey) === today;

          apiFetch(`${API_BASE_URL}/api/points/init`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username })
          }).then(() => {
            console.log('🏆 Points initialized for', username);
            // Record streak after init — only once per day
            if (alreadyRecordedToday) {
              console.log('🔥 Streak already recorded today, skipping');
              return null;
            }
            return apiFetch(`${API_BASE_URL}/api/points/streak`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username })
            });
          }).then(res => res && res.ok ? res.json() : null)
            .then(data => {
              if (data) {
                localStorage.setItem(streakKey, today); // Mark streak recorded for today
                console.log('🔥 Streak recorded:', data.streakAction, '| Current:', data.current_streak, '| Bonus:', data.streakBonus);
              }
            })
            .catch(err => console.warn('🏆 Points init/streak error (non-blocking):', err));

          await logAction('Login Success', '', '', username);
          onLoginSuccess();
        } else {
          await logAction('Login Failed', 'Invalid credentials', '', username);
          setError('Login failed. Please check your credentials.');
        }
      } else {
        await logAction('Login Failed', 'Invalid credentials', '', username);
        setError('Login failed. Please check your credentials.');
      }
    } catch (err) {
      await logAction('Login Error', err.message, '', username);
      setError('Could not connect to the login server.');
    } finally {
      setLoading(false);
    }
  };

  // Generate 60 randomly scattered stars with random sizes and blink speeds
  const stars = useMemo(() => {
    const sizes = ['small', 'medium', 'large'];
    return Array.from({ length: 120 }, (_, i) => ({
      id: i,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      size: sizes[Math.floor(Math.random() * 3)],
      delay: `${(Math.random() * 3).toFixed(2)}s`,
      duration: `${(1 + Math.random() * 2).toFixed(2)}s`,
    }));
  }, []);

  return (
    <div className="login-page">
      {/* Blinking stars — visible only during night phase */}
      <div className="stars-layer">
        {stars.map(s => (
          <div
            key={s.id}
            className={`star ${s.size}`}
            style={{
              top: s.top,
              left: s.left,
              animationDelay: s.delay,
              animationDuration: s.duration,
            }}
          />
        ))}
      </div>
      {/* Shooting stars */}
      <div className="meteor meteor-1" />
      <div className="meteor meteor-2" />
      <div className="meteor meteor-3" />
      <div className="meteor meteor-4" />
      <div className="meteor meteor-5" />
      <div className="meteor meteor-6" />
      <div className="meteor meteor-7" />
      <div className="meteor meteor-8" />
      <div className="meteor meteor-9" />
      <div className="meteor meteor-10" />
      <div className="login-card">
        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
          <img 
            src="/PySage.png" 
            alt="PySage Logo" 
            style={{ 
              height: '120px', 
              marginBottom: '12px'
            }} 
          />
        </div>
        <h1 style={{ 
          color: '#fff5b0',
          fontSize: '32px',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '28px',
          textShadow: '0 0 20px rgba(255, 230, 128, 1), 0 0 50px rgba(255, 220, 80, 0.95), 0 0 100px rgba(255, 210, 60, 0.85), 0 0 180px rgba(255, 200, 50, 0.65), 0 0 280px rgba(255, 190, 40, 0.4), 0 0 400px rgba(255, 180, 30, 0.2)',
          letterSpacing: '1px'
        }}>
          Your Python AI Tutor
        </h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && <p className="error-message">{error}</p>}
          {successMessage && <p style={{ color: '#4ade80', fontSize: '14px', textAlign: 'center', marginBottom: '12px' }}>{successMessage}</p>}
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        {INCLUDE_DEMO_LINK && (
        <button
          type="button"
          onClick={() => {
            enableDemoMode();
            onLoginSuccess();
          }}
          style={{
            marginTop: '32px',
            width: '100%',
            padding: '18px 10px',
            background: 'transparent',
            color: '#fff8c5',
            border: '1px solid rgba(255, 230, 128, 0.75)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
          }}
          title="Browse the app without an account (read-only preview)"
        >
          ✨ Explore Demo — no account needed
        </button>
        )}
        {INCLUDE_ABOUT_LINK && (
          <a
            href="/about.html"
            style={{ display: 'block', marginTop: '60px', textAlign: 'center', fontSize: '15px', color: '#ffd96a', textDecoration: 'underline' }}
          >
            About this project →
          </a>
        )}
      </div>
    </div>
  );
}

export default Login;
